import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { forexApi } from "@/lib/forex-api";
import { getStoredCurrentEnvironment } from "@/lib/environment";
import { normalizeProfitRelations, ProfitDataError, type ProfitRelation } from "./profit-dashboard-view-model";

function subscribeEnvironment(listener: () => void) {
  window.addEventListener("admin-environment-changed", listener);
  window.addEventListener("storage", listener);
  return () => {
    window.removeEventListener("admin-environment-changed", listener);
    window.removeEventListener("storage", listener);
  };
}
const environmentSnapshot = () => getStoredCurrentEnvironment()?.id || "";
interface State { environmentId: string; rows: ProfitRelation[] | null; loading: boolean; error: string; refreshedAt: number | null }
const initialState = (environmentId: string): State => ({ environmentId, rows: null, loading: true, error: "", refreshedAt: null });

function errorMessage(error: unknown) {
  if (error instanceof ProfitDataError) return error.message;
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 403) return "无权查看跟单关系";
  if (status === 401) return "登录已失效，请重新登录";
  return "跟单数据加载失败，请重试";
}

export function useProfitRelations() {
  const environmentId = useSyncExternalStore(subscribeEnvironment, environmentSnapshot, () => "");
  const [state, setState] = useState<State>(() => initialState(environmentId));
  const sequence = useRef(0);
  const controller = useRef<AbortController | null>(null);
  const refresh = useCallback(async () => {
    const request = ++sequence.current;
    controller.current?.abort();
    const abort = new AbortController();
    controller.current = abort;
    setState((old) => old.environmentId === environmentId
      ? { ...old, loading: true, error: "" } : initialState(environmentId));
    try {
      const response = await forexApi.listFollowRelations(undefined, { signal: abort.signal, timeout: 10_000 });
      const rows = normalizeProfitRelations(response);
      if (request !== sequence.current || abort.signal.aborted || environmentSnapshot() !== environmentId) return;
      setState({ environmentId, rows, loading: false, error: "", refreshedAt: Date.now() });
    } catch (error) {
      if (request !== sequence.current || abort.signal.aborted || environmentSnapshot() !== environmentId) return;
      const forbidden = [401, 403].includes((error as { response?: { status?: number } })?.response?.status ?? 0);
      setState((old) => ({ ...old, loading: false, error: errorMessage(error), ...(forbidden ? { rows: null, refreshedAt: null } : {}) }));
    }
  }, [environmentId]);
  useEffect(() => {
    void refresh();
    return () => { sequence.current += 1; controller.current?.abort(); };
  }, [refresh]);
  // Hide the previous environment immediately, before the effect starts its read.
  return { ...(state.environmentId === environmentId ? state : initialState(environmentId)), environmentId, refresh };
}
